"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _presentation, _state, _conferenceService;
Object.defineProperty(exports, "__esModule", { value: true });
exports.VideoPresentationService = exports.VideoPresentationServiceEvents = exports.VideoPresentationState = void 0;
const Service_1 = require("../Service");
const events_1 = require("../../events");
const VoxeetApi_1 = __importDefault(require("../../api/VoxeetApi"));
const VideoPresentation_1 = __importDefault(require("../../models/VideoPresentation"));
/**
 * The VideoPresentationState model represents the video presentation statuses.
 */
var VideoPresentationState;
(function (VideoPresentationState) {
    /**
     * The video presentation is played.
     */
    VideoPresentationState["PLAYING"] = "playing";
    /**
     * The video presentation is paused.
     */
    VideoPresentationState["PAUSED"] = "paused";
    /**
     * The video presentation is stopped.
     */
    VideoPresentationState["STOPPED"] = "stopped";
})(VideoPresentationState = exports.VideoPresentationState || (exports.VideoPresentationState = {}));
/**
 * A service for video presentations. It allows you to seek, pause, play, start, and stop video presentations.
 * @ignore
 */
var VideoPresentationServiceEvents;
(function (VideoPresentationServiceEvents) {
    VideoPresentationServiceEvents["Started"] = "started";
    VideoPresentationServiceEvents["Stopped"] = "stopped";
    VideoPresentationServiceEvents["Played"] = "played";
    VideoPresentationServiceEvents["Pause"] = "paused";
    VideoPresentationServiceEvents["Seek"] = "sought";
})(VideoPresentationServiceEvents = exports.VideoPresentationServiceEvents || (exports.VideoPresentationServiceEvents = {}));
/**
 * The VideoPresentationService allows sharing videos during a conference. To present a video, the conference participant needs to provide the URL that defines the video location. We recommend sharing files in the MPEG-4 Part 14 or MP4 video formats.
 *
 * **The video presentation workflow:**
 *
 * **1.** The presenter calls the [start](#start) method to start the video presentation. This method automatically starts playing the shared video file.
 *
 * **2.** All participants receive the [started](#started) event that includes in the [VideoPresentation](model/videopresentation) parameter the [URL](model/videopresentation#url) property specifying the video file location. The application includes [URL](model/videopresentation#url) in the source attribute of the [HTML video element](https://www.w3.org/TR/2011/WD-html5-20110113/video.html#video) that renders the shared video.
 *
 * **3.** The presenter can call the [pause](#pause) method to pause the shared video. In such a situation, all conference participants receive the [paused](#paused) event.
 *
 * **4.** The presenter can call the [play](#play) method to resume the paused video. In such a situation, all conference participants receive the [played](#played) event.
 *
 * **5.** The presenter can call the [seek](#seek) method to navigate to a specific section of the shared video. This method applies the provided timestamp to the [current time](https://www.w3.org/TR/2011/WD-html5-20110113/video.html#dom-media-currenttime) attribute of the HTML video element. After calling the seek method, all conference participants receive the [sought](#sought**) event and watch the video from the specified timestamp.
 *
 * **6.** The presenter calls the [stop](#stop) method to stop the video presentation.
 *
 * <br>
 *
 * ---
 * @noInheritDoc
 */
let VideoPresentationService = class VideoPresentationService extends Service_1.BaseConferenceService {
    /**
     * @ignore
     */
    constructor(sdk, conferenceService) {
        super(sdk);
        _presentation.set(this, void 0);
        _state.set(this, void 0);
        _conferenceService.set(this, void 0);
        __classPrivateFieldSet(this, _conferenceService, conferenceService);
        this.clear();
    }
    clear() {
        __classPrivateFieldSet(this, _presentation, null);
        __classPrivateFieldSet(this, _state, VideoPresentationState.STOPPED);
    }
    /**
     * Enables the video presentation and starts playing the shared video file.
     * @param url - The URL that specifies the video file location.
     */
    start(url) {
        return VoxeetApi_1.default.videoshare.start({ conferenceId: this._conferenceId, data: { url: url } }).then((r) => { });
    }
    /**
     * Stops the video presentation.
     */
    stop() {
        return VoxeetApi_1.default.videoshare.stop({ conferenceId: this._conferenceId });
    }
    /**
     * Resumes the paused video presentation.
     */
    play() {
        return VoxeetApi_1.default.videoshare.play({ conferenceId: this._conferenceId });
    }
    /**
     * Pauses the video presentation. As a timestamp, we recommend using the [currentTime](https://www.w3.org/TR/2011/WD-html5-20110113/video.html#dom-media-currenttime) attribute of the HTML video element, as in the following example:
     *
     * ```javascript
     * const timestamp = Math.round($(`#stream-video video`)[0].currentTime * 1000);
     * ```
     *
     * When the timestamp is not specified, the SDK automatically sets the timestamp value to 0.
     *
     * @param timestamp - The timestamp that informs when the video needs to be paused, in milliseconds.
     */
    pause(timestamp) {
        return VoxeetApi_1.default.videoshare.pause({ conferenceId: this._conferenceId, data: { timestamp: timestamp } });
    }
    /**
     * Allows the presenter to navigate to the specific section of the shared video.
     * @param timestamp - The timestamp the presenter wants to start playing the video from, in milliseconds.
     */
    seek(timestamp) {
        return VoxeetApi_1.default.videoshare.seek({ conferenceId: this._conferenceId, data: { timestamp: timestamp } });
    }
    onVideoPresentationStarted(e) {
        __classPrivateFieldSet(this, _presentation, new VideoPresentation_1.default(e.userId, e.url, e.timestamp));
        __classPrivateFieldGet(this, _presentation).owner = __classPrivateFieldGet(this, _conferenceService).participants.get(e.userId);
        __classPrivateFieldSet(this, _state, VideoPresentationState.PLAYING);
        this.emit(VideoPresentationServiceEvents.Started, __classPrivateFieldGet(this, _presentation));
    }
    onVideoPresentationStopped(e) {
        this.clear();
        this.emit(VideoPresentationServiceEvents.Stopped);
    }
    onVideoPresentationPlay(e) {
        __classPrivateFieldSet(this, _state, VideoPresentationState.PLAYING);
        __classPrivateFieldGet(this, _presentation).timestamp = e.timestamp;
        this.emit(VideoPresentationServiceEvents.Played, __classPrivateFieldGet(this, _presentation));
    }
    onVideoPresentationPause(e) {
        __classPrivateFieldSet(this, _state, VideoPresentationState.PAUSED);
        __classPrivateFieldGet(this, _presentation).timestamp = e.timestamp;
        this.emit(VideoPresentationServiceEvents.Pause, __classPrivateFieldGet(this, _presentation));
    }
    onVideoPresentationSeek(e) {
        __classPrivateFieldGet(this, _presentation).timestamp = e.timestamp;
        this.emit(VideoPresentationServiceEvents.Seek, __classPrivateFieldGet(this, _presentation));
    }
    /**
     * Returns information about the current video presentation. Use this accessor if you wish to receive information that is available in the [VideoPresentation](model/videopresentation) object, such as [information about the participant](doc:js-client-sdk-model-videopresentation#owner) who shares the video or the [URL](doc:js-client-sdk-model-videopresentation#url) of the presented video file.
     */
    get current() {
        return __classPrivateFieldGet(this, _presentation);
    }
    /**
     * Provides the current state of the video presentation.
     */
    get state() {
        return __classPrivateFieldGet(this, _state);
    }
};
_presentation = new WeakMap(), _state = new WeakMap(), _conferenceService = new WeakMap();
__decorate([
    Service_1.ConferenceNotNull()
], VideoPresentationService.prototype, "start", null);
__decorate([
    Service_1.ConferenceNotNull()
], VideoPresentationService.prototype, "stop", null);
__decorate([
    Service_1.ConferenceNotNull()
], VideoPresentationService.prototype, "play", null);
__decorate([
    Service_1.ConferenceNotNull()
], VideoPresentationService.prototype, "pause", null);
__decorate([
    Service_1.ConferenceNotNull()
], VideoPresentationService.prototype, "seek", null);
VideoPresentationService = __decorate([
    Service_1.Subscribe([
        events_1.EventType.VideoPresentationStarted,
        events_1.EventType.VideoPresentationStopped,
        events_1.EventType.VideoPresentationPlay,
        events_1.EventType.VideoPresentationPause,
        events_1.EventType.VideoPresentationSeek,
    ])
], VideoPresentationService);
exports.VideoPresentationService = VideoPresentationService;
